import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import PublicHeader from "../components/PublicHeader";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import {
  confirmSignupPlan,
  fetchPublicPlans,
  fetchSignupPlan,
  pickCycle,
  planText,
} from "../services/signupService";
import { validateBusinessPromo } from "../services/subscriptionService";
import { PlanPrice, PUBLIC_BACKDROP } from "./Pricing";
import { OrderSummary } from "./Register";

/**
 * Sign-up step 3, after the email is verified: the plan picked on the form,
 * with "Change plan" (same plans and prices as the pricing page — the
 * business's country decides the currency) and the promo field. Confirming
 * runs the subscription: a trial starts, a free plan is active, or a paid
 * plan starts setup mode and waits for payment. Every other screen is closed
 * until then (403 signup_plan_required).
 */
export default function SignupPlan({ onDone, onLogout }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();

  const [plans, setPlans] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [defaultCycleId, setDefaultCycleId] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [cycleId, setCycleId] = useState(null);
  const [promo, setPromo] = useState(null);
  const [promoState, setPromoState] = useState({ busy: false, error: "", notice: "" });
  const [choosing, setChoosing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectable = useMemo(() => (plans ?? []).filter((p) => p.selectable && p.type !== "custom"), [plans]);
  const plan = selectable.find((p) => p.id === planId) ?? null;
  const quote = promo?.quote ?? plan?.prices?.[cycleId] ?? null;

  const promoErrorText = useCallback(
    (err) =>
      apiFieldErrors(err).promo_code?.[0] ??
      apiFieldErrors(err).billing_cycle_id?.[0] ??
      apiErrorMessage(err, t("signup.promoFailed")),
    [t]
  );

  /** Re-check a code for a plan / cycle; a code that no longer applies is dropped, with the reason. */
  const recheckPromo = useCallback(
    async (code, forPlan, forCycle) => {
      if (!code || !forPlan || forPlan.is_free) {
        setPromo(null);
        return;
      }
      setPromoState({ busy: true, error: "", notice: "" });
      try {
        const promoQuote = await validateBusinessPromo({ code, planId: forPlan.id, cycleId: forCycle });
        setPromo({ code: promoQuote.promo?.code ?? code, quote: promoQuote });
        setPromoState({ busy: false, error: "", notice: "" });
      } catch (err) {
        setPromo(null);
        setPromoState({
          busy: false,
          error: "",
          notice: t("signup.promoRemoved", { code, reason: promoErrorText(err) }),
        });
      }
    },
    [promoErrorText, t]
  );

  useEffect(() => {
    let cancelled = false;
    // Signed in: the business's country prices the plans (same resolver as sign-up).
    Promise.all([fetchPublicPlans(), fetchSignupPlan()])
      .then(([p, requested]) => {
        if (cancelled) return;
        setPlans(p.plans);
        setCycles(p.billingCycles);
        setDefaultCycleId(p.defaultCycleId);
        const wanted = p.plans.find((x) => x.id === requested.planId && x.selectable && x.type !== "custom");
        if (wanted) {
          const cycle = pickCycle(wanted, requested.cycleId, p.defaultCycleId);
          setPlanId(wanted.id);
          setCycleId(cycle);
          if (requested.promoCode) recheckPromo(requested.promoCode, wanted, cycle);
        } else {
          // The requested plan is no longer offered: choose another.
          setChoosing(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Already done (409 signup_complete): the portal is open.
        if (err.response?.data?.code === "signup_complete") {
          onDone?.();
          navigate("/", { replace: true });
          return;
        }
        setLoadError(apiErrorMessage(err, t("signup.loadFailed")));
      });
    return () => {
      cancelled = true;
    };
    // Loaded once; the language only changes labels, which come in both languages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choosePlan = (next) => {
    const cycle = pickCycle(next, cycleId, defaultCycleId);
    setPlanId(next.id);
    setCycleId(cycle);
    setChoosing(false);
    setFormError("");
    if (promo) recheckPromo(promo.code, next, cycle);
  };

  const applyPromo = async (code) => {
    setPromoState({ busy: true, error: "", notice: "" });
    try {
      const promoQuote = await validateBusinessPromo({ code, planId, cycleId });
      setPromo({ code: promoQuote.promo?.code ?? code, quote: promoQuote });
      setPromoState({ busy: false, error: "", notice: "" });
      return true;
    } catch (err) {
      setPromoState({ busy: false, error: promoErrorText(err), notice: "" });
      return false;
    }
  };

  const changeCycle = (id) => {
    setCycleId(id);
    if (promo) recheckPromo(promo.code, plan, id);
  };

  const confirm = async () => {
    if (!plan) return;
    setSubmitting(true);
    setFormError("");
    try {
      const { user } = await confirmSignupPlan({
        planId: plan.id,
        cycleId: plan.is_free ? null : cycleId,
        promoCode: promo?.code,
      });
      onDone?.(user);
      navigate("/", { replace: true });
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (fields.promo_code && promo) {
        setPromo(null);
        setPromoState({
          busy: false,
          error: "",
          notice: t("signup.promoRemoved", { code: promo.code, reason: fields.promo_code[0] }),
        });
      } else {
        setFormError(apiErrorMessage(err, t("signupPlan.failed")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  let body;
  if (loadError) {
    body = <p className="text-danger-text">{loadError}</p>;
  } else if (plans === null) {
    body = (
      <div className="motion-safe:animate-pulse" aria-busy="true">
        <div className="mb-4 h-24 rounded-xl bg-line" />
        <div className="h-10 rounded bg-line" />
      </div>
    );
  } else if (choosing || !plan) {
    body = (
      <fieldset>
        <legend className="mb-3 text-card-title font-bold text-ink">{t("signupPlan.choose")}</legend>
        {!plan && !choosing && <p className="mb-3 text-sm text-ink-2">{t("signupPlan.requestedGone")}</p>}
        <div className="flex flex-col gap-2">
          {selectable.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => choosePlan(p)}
              aria-pressed={p.id === planId}
              className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-start ${
                p.id === planId ? "border-accent bg-accent-wash" : "border-line-strong hover:border-accent"
              }`}
            >
              <span className="min-w-0">
                <span className="flex items-center gap-2 font-semibold text-ink">
                  {p.id === planId && (
                    <CheckCircleIcon className="size-5 text-accent-text" aria-hidden="true" />
                  )}
                  {planText(p.name, lang)}
                </span>
                {p.trial_days ? (
                  <span className="block text-caption text-ink-2">
                    {t("signup.trialNote", { count: p.trial_days })}
                  </span>
                ) : null}
              </span>
              <PlanPrice plan={p} quote={p.prices?.[pickCycle(p, cycleId, defaultCycleId)]} size="sm" />
            </button>
          ))}
        </div>
        {plan && (
          <button type="button" className="btn-secondary mt-4 w-full" onClick={() => setChoosing(false)}>
            {t("signupPlan.keep", { plan: planText(plan.name, lang) })}
          </button>
        )}
      </fieldset>
    );
  } else {
    body = (
      <>
        <OrderSummary
          plan={plan}
          cycles={cycles}
          cycleId={cycleId}
          onCycle={changeCycle}
          quote={quote}
          promo={promo}
          onApplyPromo={applyPromo}
          onRemovePromo={() => {
            setPromo(null);
            setPromoState({ busy: false, error: "", notice: "" });
          }}
          promoState={promoState}
          onChangePlan={() => setChoosing(true)}
        />
        {formError && (
          <div
            className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
            role="alert"
          >
            {formError}
          </div>
        )}
        <button
          type="button"
          className="btn-primary w-full"
          onClick={confirm}
          disabled={submitting || promoState.busy || (!plan.is_free && !cycleId)}
        >
          {submitting ? t("signupPlan.submitting") : t("signupPlan.submit")}
        </button>
      </>
    );
  }

  return (
    <div className="min-h-screen" style={PUBLIC_BACKDROP}>
      <PublicHeader signIn={false} />
      <main className="mx-auto w-full max-w-xl px-4 pb-16 pt-4 sm:px-6 sm:pt-8">
        <p className="mb-1 inline-flex items-center gap-1 text-sm font-semibold text-accent-text">
          <CheckCircleIcon className="size-4" aria-hidden="true" />
          {t("signupPlan.verified")}
        </p>
        <h1 className="text-page-title font-bold text-ink">{t("signupPlan.title")}</h1>
        <p className="mb-6 mt-1 text-body text-ink-2">{t("signupPlan.subtitle")}</p>
        <div className="card p-5 sm:p-6">{body}</div>
        <p className="mt-6 text-center text-sm text-ink-2">
          <button type="button" className="font-semibold text-accent-text hover:underline" onClick={onLogout}>
            {t("signupPlan.logout")}
          </button>
        </p>
      </main>
    </div>
  );
}
